'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { FileText, CheckCircle, Clock, AlertTriangle, XCircle, Search, Download } from 'lucide-react';

const FILINGS = [
  { id: 'FIL-2025-048291', taxpayer: 'Kollie Trading Co.', tin: 'TIN-2024-001847', type: 'Corporate Income Tax', period: 'Q1 2025', submitted: '2025-04-14', status: 'approved', amount: '$84,200', county: 'Montserrado' },
  { id: 'FIL-2025-047103', taxpayer: 'Maryland Fisheries Ltd.', tin: 'TIN-2024-011205', type: 'VAT Return', period: 'Mar 2025', submitted: '2025-04-10', status: 'approved', amount: '$22,800', county: 'Maryland' },
  { id: 'FIL-2025-046882', taxpayer: 'LPA Holdings Ltd.', tin: 'TIN-2024-009034', type: 'Corporate Income Tax', period: 'Q1 2025', submitted: '2025-04-28', status: 'under-review', amount: '$156,400', county: 'Grand Bassa' },
  { id: 'FIL-2025-045671', taxpayer: 'Margibi Industrial Corp.', tin: 'TIN-2024-031002', type: 'Withholding Tax', period: 'Mar 2025', submitted: '2025-04-05', status: 'under-review', amount: '$31,700', county: 'Margibi' },
  { id: 'FIL-2025-044290', taxpayer: 'Gedeh Commerce Ltd.', tin: 'TIN-2024-018923', type: 'VAT Return', period: 'Mar 2025', submitted: '2025-04-08', status: 'approved', amount: '$18,300', county: 'Grand Gedeh' },
  { id: 'FIL-2025-043018', taxpayer: 'Sinoe Timber Co.', tin: 'TIN-2024-022341', type: 'Corporate Income Tax', period: 'Q1 2025', submitted: '2025-05-02', status: 'flagged', amount: '$9,100', county: 'Sinoe' },
  { id: 'FIL-2025-041774', taxpayer: 'Fatima Kamara', tin: 'TIN-2024-025678', type: 'Personal Income Tax', period: 'FY 2024', submitted: '2025-03-28', status: 'approved', amount: '$4,800', county: 'Lofa' },
  { id: 'FIL-2025-040512', taxpayer: 'James Kollie', tin: 'TIN-2024-014567', type: 'Personal Income Tax', period: 'FY 2024', submitted: null, status: 'overdue', amount: '—', county: 'Nimba' },
  { id: 'FIL-2025-039201', taxpayer: 'Mary Pewee', tin: 'TIN-2024-003291', type: 'Personal Income Tax', period: 'FY 2024', submitted: null, status: 'overdue', amount: '—', county: 'Montserrado' },
  { id: 'FIL-2025-037884', taxpayer: 'Bong County Dev. Corp.', tin: 'TIN-2024-007812', type: 'Corporate Income Tax', period: 'Q1 2025', submitted: '2025-04-01', status: 'approved', amount: '$0', county: 'Bong' },
];

const statusConfig: Record<string, { color: string; bg: string; label: string; icon: React.ElementType }> = {
  approved: { color: '#34d399', bg: 'rgba(52,211,153,0.12)', label: 'Approved', icon: CheckCircle },
  'under-review': { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)', label: 'Under Review', icon: Clock },
  flagged: { color: '#f87171', bg: 'rgba(248,113,113,0.12)', label: 'Flagged', icon: AlertTriangle },
  overdue: { color: '#fb923c', bg: 'rgba(251,146,60,0.12)', label: 'Overdue', icon: XCircle },
};

const kpis = [
  { label: 'Total Filings (YTD)', value: '48,291', color: 'var(--accent)' },
  { label: 'Approved', value: '41,847', color: '#34d399' },
  { label: 'Under Review', value: '3,204', color: '#60a5fa' },
  { label: 'Overdue / Flagged', value: '3,240', color: '#f87171' },
];

export default function TaxFilingsClient() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');

  const types = ['All', 'Corporate Income Tax', 'VAT Return', 'Personal Income Tax', 'Withholding Tax'];

  const filtered = FILINGS.filter((f) => {
    const matchSearch = !search || f.taxpayer.toLowerCase().includes(search.toLowerCase()) || f.id.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'All' || f.status === statusFilter.toLowerCase().replace(' ', '-');
    const matchType = typeFilter === 'All' || f.type === typeFilter;
    return matchSearch && matchStatus && matchType;
  });

  return (
    <AppLayout>
      <div className="min-h-screen px-4 py-5 lg:px-6 xl:px-8 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)' }}>
              <FileText size={20} style={{ color: '#070e1f' }} />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold" style={{ color: 'var(--dark-text)' }}>Tax Filings</h1>
              <p className="text-xs sm:text-sm" style={{ color: 'var(--dark-muted)' }}>Returns, assessments, and filing status across all tax types</p>
            </div>
          </div>
          <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
            style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)', border: '1px solid rgba(212,160,23,0.3)' }}>
            <Download size={13} />
            Export Report
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {kpis.map((k) => (
            <div key={k.label} className="rounded-xl px-4 py-4"
              style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
              <p className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{k.label}</p>
              <p className="text-2xl font-bold font-mono-data" style={{ color: k.color }}>{k.value}</p>
            </div>
          ))}
        </div>

        {/* Filters + Table */}
        <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
          <div className="flex items-center gap-3 px-4 py-3 border-b flex-wrap" style={{ borderColor: 'var(--dark-border)' }}>
            <div className="relative flex-1 min-w-48">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--dark-muted)' }} />
              <input type="text" placeholder="Search taxpayer or filing ID…" value={search} onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 rounded-md text-xs outline-none"
                style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }} />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {['All', 'Approved', 'Under Review', 'Flagged', 'Overdue'].map((s) => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className="px-2.5 py-1 rounded-full text-xs font-medium transition-all"
                  style={{
                    backgroundColor: statusFilter === s ? 'rgba(212,160,23,0.15)' : 'var(--dark-elevated)',
                    color: statusFilter === s ? 'var(--accent)' : 'var(--dark-muted)',
                    border: `1px solid ${statusFilter === s ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
                  }}>
                  {s}
                </button>
              ))}
            </div>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-1.5 rounded-md text-xs outline-none appearance-none"
              style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }}>
              {types.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--dark-border)' }}>
                  {['Filing ID', 'Taxpayer', 'Type', 'Period', 'Submitted', 'Amount', 'County', 'Status'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left font-semibold" style={{ color: 'var(--dark-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((f) => {
                  const sc = statusConfig[f.status];
                  const SIcon = sc.icon;
                  return (
                    <tr key={f.id} className="transition-colors"
                      style={{ borderBottom: '1px solid var(--dark-border)' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.02)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                    >
                      <td className="px-4 py-3 font-mono-data font-semibold" style={{ color: 'var(--accent)' }}>{f.id}</td>
                      <td className="px-4 py-3 font-medium" style={{ color: 'var(--dark-text)' }}>{f.taxpayer}</td>
                      <td className="px-4 py-3" style={{ color: 'var(--dark-muted)' }}>{f.type}</td>
                      <td className="px-4 py-3" style={{ color: 'var(--dark-muted)' }}>{f.period}</td>
                      <td className="px-4 py-3" style={{ color: f.submitted ? 'var(--dark-muted)' : '#f87171' }}>{f.submitted || 'Not submitted'}</td>
                      <td className="px-4 py-3 font-mono-data font-semibold" style={{ color: 'var(--dark-text)' }}>{f.amount}</td>
                      <td className="px-4 py-3" style={{ color: 'var(--dark-muted)' }}>{f.county}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <SIcon size={12} style={{ color: sc.color }} />
                          <span className="px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: sc.bg, color: sc.color }}>{sc.label}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
